import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, PackageSearch, Factory, Box, Settings, Users, BarChart2, Menu, } from 'lucide-react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, styled, IconButton, useMediaQuery, useTheme } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const sidebarItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { icon: PackageSearch, label: 'Orders Management', path: '/admin/orders' },
  { icon: Factory, label: 'Production Tracking', path: '/admin/production-tracking' },
  { icon: Box, label: 'Stock Management', path: '/admin/stock-management' },
  { icon: Settings, label: 'Product Management', path: '/admin/machine-management' },
  { icon: Users, label: 'User Management', path: '/admin/user-management' },
  { icon: BarChart2, label: 'Damage/Shortage', path: '/admin/damage-shortage' },
  { icon: AccountCircleIcon, label: 'Profile', path: '/admin/profile' },
];

const SidebarDrawer = styled(Drawer)(({ theme }) => ({
  width: 256,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 256,
    height: 'calc(100vh - 80px)', // Adjusted height
    marginTop: '80.4px', // Push below navbar
    boxSizing: 'border-box',
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    padding: theme.spacing(2),
  },
}));


const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s',
  margin: theme.spacing(0.5, 0),
  '&.active': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: 'scale(1.05)',
    '& .MuiListItem-root': {
      color: theme.palette.primary.main,
    }
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.05)',
  }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  top: 10,
  right: 10,
  zIndex: 1200,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const Sidebar = ({ items = sidebarItems }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const sidebarContent = (
    <List>
      {items.map((item, index) => (
        <StyledNavLink
          key={item.path}
          to={item.path}
          className={({ isActive }) => isActive ? 'active' : ''}
          onClick={isMobile ? handleDrawerToggle : undefined}
        >
          <StyledListItem>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon size={20} />
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </StyledListItem>
        </StyledNavLink>
      ))}
    </List>
  );

  // Mobile view with menu button
  if (isMobile) {
    return (
      <>
        <MenuButton
          color="primary"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
        >
          <Menu size={24} />
        </MenuButton>

        <Drawer
          anchor="left"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 256,
              boxSizing: 'border-box',
              padding: theme.spacing(2)
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  // Desktop view with permanent drawer
  return (
    <SidebarDrawer
      variant="permanent"
      anchor="left"
    >
      {sidebarContent}
    </SidebarDrawer>
  );
}

export default Sidebar;
